import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  TrendingUp, 
  AlertTriangle,
  Calendar,
  Timer,
  ChevronRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OvertimeProgressCard = ({ employeeId = null, compact = false }) => {
  const [periodInfo, setPeriodInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPeriodInfo();
  }, [employeeId]);

  const fetchPeriodInfo = async () => {
    try {
      setLoading(true);
      const endpoint = employeeId 
        ? `${API}/overtime/employee/${employeeId}/current-period`
        : `${API}/overtime/current-period`;
      
      const response = await axios.get(endpoint);
      setPeriodInfo(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch period info:', err);
      setError('Failed to load overtime data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={`border-0 shadow-lg ${compact ? 'p-3' : ''}`}>
        <CardContent className="flex items-center justify-center py-8">
          <div className="spinner" />
        </CardContent>
      </Card>
    );
  }

  if (error || !periodInfo) {
    return null;
  }

  const {
    period_type,
    period_start,
    period_end,
    standard_hours,
    hours_worked,
    hours_remaining,
    overtime_hours,
    progress_percent,
    projected_hours,
    projected_overtime,
    is_overtime_warning,
    days_in_period,
    days_elapsed,
    days_remaining
  } = periodInfo;

  const formatDate = (isoString) => {
    try {
      // Extract date directly from ISO string to preserve the original timezone's date
      // Instead of parsing and converting, just extract YYYY-MM-DD
      const dateStr = isoString.substring(0, 10); // Get YYYY-MM-DD
      const [year, month, day] = dateStr.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month) - 1]} ${parseInt(day)}`;
    } catch {
      return isoString;
    }
  };

  const getProgressColor = () => {
    if (overtime_hours > 0) return 'bg-red-500';
    if (progress_percent >= 90) return 'bg-amber-500';
    if (progress_percent >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusBadge = () => {
    if (overtime_hours > 0) {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-200">
          <AlertTriangle className="w-3 h-3 mr-1" />
          {overtime_hours.toFixed(1)}h overtime
        </Badge>
      );
    }
    if (is_overtime_warning) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Approaching limit
        </Badge>
      );
    }
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200">
        On track
      </Badge>
    );
  };

  if (compact) {
    return (
      <div className="p-4 bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium capitalize">{period_type} Hours</span>
          </div>
          {getStatusBadge()}
        </div>
        <div className="mb-2">
          <Progress value={Math.min(progress_percent, 100)} className="h-2" />
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{hours_worked.toFixed(1)}h / {standard_hours}h</span>
          <span>{days_remaining} days left</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-0 shadow-lg" data-testid="overtime-progress-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Manrope'] flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Period Progress
          </CardTitle>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Period Info */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Calendar className="w-4 h-4" />
            <span className="capitalize">{period_type}</span>
          </div>
          <span className="text-slate-500">
            {formatDate(period_start)} <ChevronRight className="w-3 h-3 inline" /> {formatDate(period_end)}
          </span>
        </div>

        {/* Main Progress */}
        <div className="space-y-2">
          <div className="flex justify-between items-end">
            <div>
              <span className="text-3xl font-bold text-slate-900">{hours_worked.toFixed(1)}</span>
              <span className="text-slate-500 text-lg"> / {standard_hours}h</span>
            </div>
            <span className="text-lg font-medium text-slate-700">{progress_percent.toFixed(0)}%</span>
          </div>
          <div className="relative">
            <Progress value={Math.min(progress_percent, 100)} className="h-3" />
            <div 
              className={`absolute top-0 left-0 h-3 rounded-full transition-all ${getProgressColor()}`}
              style={{ width: `${Math.min(progress_percent, 100)}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-xl font-semibold text-slate-900">{hours_remaining.toFixed(1)}</div>
            <div className="text-xs text-slate-500">Hours Left</div>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-xl font-semibold text-slate-900">{days_remaining}</div>
            <div className="text-xs text-slate-500">Days Left</div>
          </div>
          <div className={`text-center p-3 rounded-lg ${overtime_hours > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
            <div className={`text-xl font-semibold ${overtime_hours > 0 ? 'text-red-600' : 'text-slate-900'}`}>
              {overtime_hours > 0 ? `+${overtime_hours.toFixed(1)}` : '0'}
            </div>
            <div className="text-xs text-slate-500">Overtime</div>
          </div>
        </div>

        {/* Projection (if available) */}
        {projected_hours && days_remaining > 0 && (
          <div className={`p-3 rounded-lg text-sm ${
            projected_overtime > 0 
              ? 'bg-amber-50 border border-amber-200' 
              : 'bg-green-50 border border-green-200'
          }`}>
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${projected_overtime > 0 ? 'text-amber-600' : 'text-green-600'}`} />
              <span className={projected_overtime > 0 ? 'text-amber-800' : 'text-green-800'}>
                {projected_overtime > 0 
                  ? `Projected ${projected_overtime.toFixed(1)}h overtime at current pace`
                  : `On track to finish within ${standard_hours}h`
                }
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OvertimeProgressCard;
